import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, FileText, MessageSquare, Loader2, CheckCircle2, Lock, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/signature-membre`;
const err = (e, d) => e?.response?.data?.detail || d;

const EtapeSignature = ({ numero, titre, doc, signee, verrou, token, etape, onSigned }) => {
  const [envoye, setEnvoye] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(null);
  const ouvrir = async () => {
    setBusy('doc');
    try {
      const r = await axios.get(`${API}/${token}/document/${doc}`, { responseType: 'blob' });
      window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
    } catch (e) { toast.error('Document indisponible'); } finally { setBusy(null); }
  };
  const demander = async () => {
    setBusy('code');
    try { const { data } = await axios.post(`${API}/${token}/${etape}/code`); setEnvoye(data.telephone_masque); toast.success(`Code envoyé au ${data.telephone_masque}`); }
    catch (e) { toast.error(err(e, "Envoi du code impossible")); } finally { setBusy(null); }
  };
  const valider = async () => {
    setBusy('valider');
    try { await axios.post(`${API}/${token}/${etape}/valider`, { code }); toast.success(`${titre} signé(e)`); onSigned(); }
    catch (e) { toast.error(err(e, 'Code incorrect')); } finally { setBusy(null); }
  };
  const t = (x) => `signature-membre-${etape}-${x}`;
  return (
    <section className={`rounded-2xl border p-6 space-y-4 transition-colors ${signee ? 'border-emerald-200 bg-emerald-50/60' : verrou ? 'border-slate-200 bg-slate-50 opacity-70' : 'border-indigo-200 bg-white shadow-sm'}`} data-testid={t('bloc')}>
      <div className="flex items-center gap-3">
        <span className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold text-white ${signee ? 'bg-emerald-600' : verrou ? 'bg-slate-400' : 'bg-indigo-600'}`}>{signee ? <CheckCircle2 className="h-4 w-4" /> : numero}</span>
        <h2 className="text-base md:text-lg font-semibold text-slate-900">{titre}</h2>
        {verrou && !signee && <span className="ml-auto flex items-center gap-1 text-xs text-amber-700"><Lock className="h-3.5 w-3.5" /> Après la FTPE</span>}
      </div>
      {signee ? <p className="text-sm text-emerald-800" data-testid={t('signee')}>Signé(e) électroniquement.</p> : !verrou && (
        <div className="space-y-3">
          <Button variant="outline" onClick={ouvrir} disabled={!!busy} className="gap-2" data-testid={t('document')}>
            {busy === 'doc' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Lire le document
          </Button>
          {!envoye ? (
            <Button onClick={demander} disabled={!!busy} className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 gap-2" data-testid={t('code')}>
              {busy === 'code' ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />} Recevoir mon code par SMS
            </Button>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-slate-600">Code envoyé au {envoye}. En le saisissant, vous signez électroniquement ce document.</p>
              <div className="flex gap-2">
                <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} maxLength={6} placeholder="Code à 6 chiffres" className="h-12 text-center font-mono text-lg tracking-[0.4em]" data-testid={t('input')} />
                <Button onClick={valider} disabled={!!busy || code.length < 6} className="h-12 px-6 bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid={t('valider')}>
                  {busy === 'valider' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Signer
                </Button>
              </div>
              <button type="button" onClick={demander} disabled={!!busy} className="text-xs text-indigo-600 underline" data-testid={t('renvoyer')}>Renvoyer le code</button>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default function SignatureMembrePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [erreur, setErreur] = useState(null);
  const charger = useCallback(() => axios.get(`${API}/${token}`).then((r) => setInfo(r.data)).catch((e) => setErreur(err(e, 'Lien invalide'))), [token]);
  useEffect(() => { charger(); }, [charger]);

  if (erreur) return (
    <main className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md rounded-2xl border border-amber-200 bg-white p-8 text-center space-y-3" data-testid="signature-membre-erreur">
        <AlertTriangle className="mx-auto h-8 w-8 text-amber-500" /><p className="text-slate-800">{erreur}</p>
      </div>
    </main>
  );
  if (!info) return <main className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></main>;
  const ftpeOk = !info.ftpe.requise || info.ftpe.signee;
  return (
    <main className="min-h-screen bg-slate-50 px-4 py-10" data-testid="signature-membre-page">
      <div className="mx-auto max-w-xl space-y-5">
        <header className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-widest text-indigo-600">COPPAM — Signature électronique</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">{info.raison_sociale}</h1>
          <p className="text-sm text-slate-600" data-testid="signature-membre-ref">Dispositif {info.dispositif} · {info.numero_police}</p>
          <p className="text-xs text-slate-400">Lien valable jusqu'au {new Date(info.expires_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</p>
        </header>
        {info.ftpe.deja_adherent && <p className="rounded-lg bg-emerald-50 px-4 py-2 text-sm text-emerald-800" data-testid="signature-membre-adherent">Vous êtes déjà adhérent FTPE à jour : seul le dispositif est à signer.</p>}
        {info.ftpe.requise && <EtapeSignature numero={1} titre="Adhésion FTPE" doc="ftpe" etape="ftpe" signee={info.ftpe.signee} token={token} onSigned={charger} />}
        <EtapeSignature numero={info.ftpe.requise ? 2 : 1} titre={`Dispositif ${info.dispositif}`} doc="dispositif" etape="dispositif"
          signee={info.dispositif_signe} verrou={!ftpeOk} token={token} onSigned={charger} />
        {info.dispositif_signe && <p className="text-center text-sm text-emerald-700" data-testid="signature-membre-termine">Merci, votre signature est enregistrée.</p>}
      </div>
    </main>
  );
}
